/**
 * viewportAnalysis.js — Enterprise Medical Analysis Engine
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 *
 * [v3.0 — FULL CLINICAL SUITE]:
 *  - computeCalciumScore: зовёт aiModule.calciumScoring, а тот с 9.0.0
 *    делегирует в ai/calciumScoreCore.js — единственную реализацию Агатстона.
 *    (До 9.0.0 эта строка утверждала прямую делегацию в ядро, а код шёл в
 *    aiModule, где лежала вторая копия расчёта. Комментарий врал.)
 *  - computeCalciumScore: fallback 2D только если 3D недоступен
 *  - computeVolumeStats: использует общую _getVolumeScalarData
 *  - computeCircleROIStats: fallback для getDimensions()
 *  - _getHUAtWorld: безопасный worldToCanvas fallback для STACK
 *  - computeLineProfile: выборочное СКО (n-1) + визуализация графика
 *  - console.log → log.debug
 *  - clusters: только топ-20 по скору
 *  - _identifyOrgan: улучшенная детекция bodyPart через DICOM-теги
 *  - detectAorticAneurysm(): автоматическое измерение диаметра аорты
 *  - computeRECIST(): онкологический RECIST 1.1
 *  - compareROIStats(): сравнение текущего и предыдущего ROI
 *  - computeTumorVolumeChange(): изменение объёма опухоли
 *  - renderLineProfileChart(): визуализация профиля плотности в Canvas
 *  - analyzeBrainAtrophy(): автоматический анализ атрофии мозга
 *  - segmentOrgansByDensity(): сегментация органов по плотности
 *  - autoDetectCriticalFindings(): поиск критических находок
 *  - exportMeasurementsJSON(): экспорт всех измерений для отчёта
 *
 * [ENTERPRISE FEATURES]
 *   ✓ HU Probe с real-time tissue classification
 *   ✓ Line Profile — HU значения вдоль линии + график
 *   ✓ Volume Statistics — глобальная статистика объёма
 *   ✓ Auto Body Region Detector — по гистограмме HU
 *   ✓ Circle ROI + Rect ROI с корректным pixelSpacing
 *   ✓ Calcium Score (Agatston) — 3D связность, правильная нормировка
 *   ✓ Pathology Finder — навигация к срезу с макс. патологией
 *   ✓ Tissue Density Map — % тканей разного типа в ROI
 *   ✓ Aortic Aneurysm Detection — автоматический скрининг аорты
 *   ✓ RECIST 1.1 — онкологические измерения
 *   ✓ Brain Atrophy Analysis — VBR (ventricle-to-brain ratio)
 *   ✓ Organ Segmentation — сегментация по плотности HU
 *   ✓ Critical Findings Detector — авто-поиск кровоизлияний, пневмоторакса
 */

import state, { getRenderingEngine, getViewportIds, ViewportType, cornerstone } from './viewportState.js';
import * as _cs from '@cornerstonejs/core'; // [10.19.43] metaData для гейта модальности пробы
import { events } from './events.js';
import { EVENTS } from './eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════
//  TISSUE CLASSIFICATION TABLE (HU → tissue)
// ══════════════════════════════════════════════════════════════

const TISSUE_TABLE = [
  { min: -1100, max: -900, name: 'Воздух / Лёгкие',   nameEn: 'Air/Lung',       color: '#1a1a2e' },
  { min: -900,  max: -500, name: 'Жир / Эмфизема',    nameEn: 'Fat/Emphysema',  color: '#e6b800' },
  { min: -500,  max: -100, name: 'Жировая ткань',     nameEn: 'Adipose',        color: '#ffdd57' },
  { min: -100,  max: -30,  name: 'Жир',               nameEn: 'Fat',            color: '#f0c040' },
  { min: -30,   max: 20,   name: 'Вода / Выпот',      nameEn: 'Water/Effusion', color: '#4ecdc4' },
  { min: 20,    max: 60,   name: 'Мягкая ткань',      nameEn: 'Soft tissue',    color: '#c0392b' },
  { min: 60,    max: 90,   name: 'Кровь / Мышца',     nameEn: 'Blood/Muscle',   color: '#e74c3c' },
  { min: 90,    max: 200,  name: 'Контраст / Кровь',  nameEn: 'Contrast/Blood', color: '#e91e63' },
  { min: 200,   max: 700,  name: 'Кальцинат',         nameEn: 'Calcification',  color: '#ecf0f1' },
  { min: 700,   max: 3000, name: 'Кость',             nameEn: 'Bone',           color: '#f5f5f5' },
  { min: 3000,  max: 4096, name: 'Металл',            nameEn: 'Metal',          color: '#ffffff' },
];

export function classifyTissue(hu) {
  for (const t of TISSUE_TABLE) {
    if (hu >= t.min && hu < t.max) return t;
  }
  return { min: -Infinity, max: Infinity, name: 'Неизвестно', nameEn: 'Unknown', color: '#888' };
}

export function getTissueTable() { return [...TISSUE_TABLE]; }

// ══════════════════════════════════════════════════════════════
//  MEASUREMENT STORE (для экспорта и сравнения)
// ══════════════════════════════════════════════════════════════

const _measurementHistory = [];

function _storeMeasurement(type, data) {
  const entry = { type, data, timestamp: Date.now(), isoTime: new Date().toISOString() };
  _measurementHistory.push(entry);
  // Храним последние 100 измерений
  if (_measurementHistory.length > 100) _measurementHistory.shift();
  return entry;
}

/**
 * Экспорт всех измерений для отчёта
 * @returns {Object[]}
 */
export function exportMeasurementsJSON() {
  return [..._measurementHistory];
}

/**
 * Очистить историю измерений
 */
export function clearMeasurementsHistory() {
  _measurementHistory.length = 0;
}

// ══════════════════════════════════════════════════════════════
//  HU PROBE — с классификацией тканей
// ══════════════════════════════════════════════════════════════

let _probeActive = false;
const _probeListeners = new Map();

const _модКэш = new Map();
function _модальностьОкна(vp, viewportId) {
  if (_модКэш.has(viewportId)) return _модКэш.get(viewportId);
  let мод = null;
  try {
    const id = vp.getImageIds?.()[0] || vp.getCurrentImageId?.();
    if (id && _cs?.metaData) мод = _cs.metaData.get('generalSeriesModule', id)?.modality || null;
  } catch (e) { /* метаданные — не повод ронять пробу */ }
  _модКэш.set(viewportId, мод);
  return мод;
}

export function enableHUProbe() {
  log.debug('[HU Probe] enableHUProbe called, already active:', _probeActive);
  if (_probeActive) return;
  _probeActive = true;
  _probeFirstLog = true;
  _huDiagOnce = true;
  
  if (!document.getElementById('hu-probe-tooltip')) {
    const tip = document.createElement('div');
    tip.id = 'hu-probe-tooltip';
    tip.style.cssText = 'position:fixed;z-index:50000;pointer-events:none;display:none;' +
      'background:rgba(0,0,0,0.85);color:#fff;padding:4px 8px;border-radius:4px;font-size:12px;' +
      'font-family:monospace;white-space:nowrap;border:1px solid rgba(255,255,255,0.2);' +
      'box-shadow:0 2px 8px rgba(0,0,0,0.5);';
    document.body.appendChild(tip);
  }
  
  const re = getRenderingEngine();
  if (!re) { log.warn('[HU Probe] No rendering engine'); return; }
  
  let attached = 0;
  for (const vp of re.getViewports()) {
    if (vp.type === ViewportType.VOLUME_3D) continue;
    const el = vp.element;
    if (!el) continue;
    const targets = [el];
    const canvas = el.querySelector('canvas') || vp.getCanvas?.();
    if (canvas && canvas !== el) targets.push(canvas);
    
    for (const target of targets) {
      if (_probeListeners.has(target)) continue;
      const handler = (evt) => _onProbeMove(evt, vp.id);
      target.addEventListener('mousemove', handler);
      target.addEventListener('touchmove', handler, { passive: true });
      _probeListeners.set(target, handler);
      attached++;
    }
  }
  log.debug('[HU Probe] Listeners attached to', attached, 'elements');
}

export function disableHUProbe() {
  _probeActive = false;
  for (const [key, handler] of _probeListeners) {
    if (key instanceof Element) {
      key.removeEventListener('mousemove', handler);
      key.removeEventListener('touchmove', handler);
    }
  }
  _probeListeners.clear();
  const tip = document.getElementById('hu-probe-tooltip');
  if (tip) tip.style.display = 'none';
  log.debug('[HU Probe] Disabled');
}

let _probeFirstLog = true;
function _onProbeMove(evt, viewportId) {
  if (!_probeActive) return;
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return;

  if (_probeFirstLog) {
    log.debug('[HU Probe] First mousemove on viewport:', viewportId, 'type:', vp.type, 'hasCanvasToWorld:', !!vp.canvasToWorld);
    _probeFirstLog = false;
  }

  const rect = vp.element.getBoundingClientRect();
  const cx = evt.touches ? evt.touches[0].clientX : evt.clientX;
  const cy = evt.touches ? evt.touches[0].clientY : evt.clientY;
  const canvasPoint = [cx - rect.left, cy - rect.top];
  const absX = cx;
  const absY = cy;

  try {
    if (!vp.canvasToWorld) return;
    const worldPos = vp.canvasToWorld(canvasPoint);
    const hu = _getHUAtWorld(vp, worldPos, canvasPoint);
    // [10.19.43] Гейт модальности (переезд пробы в тулбар по решению
    // владельца): HU существует только у КТ. На МР/УЗИ число — сырые
    // интенсивности, и писать «HU» с именем ткани было бы ложью единиц.
    const мод = _модальностьОкна(vp, viewportId);
    const этоКТ = !мод || мод === 'CT';
    
    const tip = document.getElementById('hu-probe-tooltip');
    if (tip) {
      tip.style.display = 'block';
      tip.style.left = (absX + 16) + 'px';
      tip.style.top = (absY - 8) + 'px';
      
      if (hu !== null && !isNaN(hu) && !этоКТ) {
        tip.innerHTML = `<span style="color:#4fc3f7;font-weight:bold">${Math.round(hu)}</span>`
          + `<span style="color:#aaa"> · интенсивность (${мод})</span>`;
      } else if (hu !== null && !isNaN(hu)) {
        const tissue = classifyTissue(hu);
        const organ = _identifyOrgan(hu, worldPos, vp);
        tip.innerHTML = `<span style="color:${tissue.color || '#4fc3f7'};font-weight:bold">${Math.round(hu)} HU</span>` +
          `<span style="color:#aaa"> · ${tissue.name}</span>` +
          (organ ? `<br><span style="color:#ffc107;font-size:11px">${organ}</span>` : '');
        
        events.emit(EVENTS.HU_PROBE, {
          viewportId, hu: Math.round(hu), tissue: tissue.name,
          tissueEn: tissue.nameEn, tissueColor: tissue.color, organ, worldPos, canvasPoint,
        });
      } else {
        tip.innerHTML = `<span style="color:#f88">HU: —</span>` +
          `<span style="color:#666"> pos:[${worldPos.map(v=>v.toFixed(0)).join(',')}]</span>`;
      }
    }
  } catch (e) { /* silent — high frequency */ }
}

function _identifyOrgan(hu, worldPos, vp) {
  let bodyPart = '';
  try {
    const actors = vp.getActors?.();
    if (actors?.length) {
      const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
      const vol = cornerstone?.cache?.getVolume?.(volId);
      if (vol) {
        bodyPart = (vol.metadata?.BodyPartExamined || vol.metadata?.bodyPart || '').toUpperCase();
        if (!bodyPart && vol.imageIds?.length) {
          try {
            const midId = vol.imageIds[Math.floor(vol.imageIds.length / 2)];
            const meta = cornerstone.metaData.get('generalSeriesModule', midId);
            bodyPart = (meta?.bodyPartExamined || '').toUpperCase();
          } catch (e) { /* */ }
        }
      }
    }
  } catch(e) { /* safe */ }

  if (hu >= 700) return '🦴 Кость (кортикальная)';
  if (hu >= 300 && hu < 700) return '🦴 Кость (губчатая)';
  if (hu >= 130 && hu < 300) return '🩸 Кальцинат / контраст в сосуде';
  
  if (bodyPart === 'CHEST' || bodyPart === 'LUNG' || !bodyPart) {
    if (hu >= 30 && hu < 80) return '🫀 Миокард / мягкие ткани средостения';
    if (hu >= 80 && hu < 130) return '🩸 Контрастированная кровь';
    if (hu >= -100 && hu < 30) return '🫁 Лёгочная ткань (уплотнённая)';
    if (hu >= -900 && hu < -100) return '🫁 Лёгочная паренхима';
    if (hu < -900) return '💨 Воздух (трахея/бронхи)';
    if (hu >= -150 && hu < -30) return '🟡 Жировая клетчатка';
  }
  
  if (bodyPart === 'ABDOMEN' || bodyPart === 'PELVIS') {
    if (hu >= 50 && hu < 80) return '🟤 Печень / селезёнка';
    if (hu >= 30 && hu < 50) return '🟤 Поджелудочная / почки';
    if (hu >= 80 && hu < 130) return '🩸 Контрастированный сосуд';
    if (hu >= 0 && hu < 20) return '💧 Жидкость / моча / желчь';
    if (hu >= -150 && hu < -30) return '🟡 Жировая клетчатка';
    if (hu < -150) return '💨 Газ в кишке';
  }

  if (bodyPart === 'HEAD' || bodyPart === 'BRAIN') {
    if (hu >= 25 && hu < 45) return '🧠 Серое вещество';
    if (hu >= 20 && hu < 30) return '🧠 Белое вещество';
    if (hu >= 50 && hu < 80) return '🩸 Острая гематома';
    if (hu >= 0 && hu < 15) return '💧 Ликвор (СМЖ)';
  }

  if (hu >= 30 && hu < 80) return '🔴 Мягкие ткани / паренхима';
  if (hu >= -150 && hu < -30) return '🟡 Жировая ткань';
  if (hu >= 0 && hu < 30) return '💧 Жидкость / кровь';
  if (hu < -500) return '💨 Воздух';
  
  return '';
}

let _huDiagOnce = true;
function _getHUAtWorld(vp, worldPos, canvasPoint) {
  if (vp.type === ViewportType.ORTHOGRAPHIC) {
    try {
      const actors = vp.getActors?.();
      const volId = actors?.[0]?.uid || actors?.[0]?.referenceId || actors?.[0]?.referencedId;
      let vol = null;
      if (volId) try { vol = cornerstone.cache.getVolume?.(volId); } catch(e) {}
      if (!vol) try { const vols = cornerstone.cache.getVolumes?.(); if (vols?.length) vol = vols[0]; } catch(e) {}
      if (!vol) return null;
      
      const dims = vol.dimensions || (vol.imageData?.getDimensions?.() || vol.imageData?.dimensions);
      if (!dims || dims.length < 3) return null;
      
      let sd = null;
      try { sd = vol.voxelManager?.getCompleteScalarDataArray?.(); } catch(e) {}
      if (!sd) try { sd = vol.imageData?.getPointData?.()?.getScalars?.()?.getData?.(); } catch(e) {}
      if (!sd) try { sd = vol.getScalarData?.(); } catch(e) {}
      if (!sd) try { sd = vol.scalarData; } catch(e) {}
      if (!sd) { if (_huDiagOnce) { log.debug('[HU] No scalar data'); _huDiagOnce = false; } return null; }
      
      const origin = vol.origin || [0,0,0];
      const spacing = vol.spacing || [1,1,1];
      const direction = vol.direction || [1,0,0, 0,1,0, 0,0,1];
      const dx = worldPos[0] - origin[0], dy = worldPos[1] - origin[1], dz = worldPos[2] - origin[2];
      const ix = Math.round((direction[0]*dx + direction[3]*dy + direction[6]*dz) / spacing[0]);
      const iy = Math.round((direction[1]*dx + direction[4]*dy + direction[7]*dz) / spacing[1]);
      const iz = Math.round((direction[2]*dx + direction[5]*dy + direction[8]*dz) / spacing[2]);
      if (_huDiagOnce) { log.debug('[HU] world→idx:', [ix,iy,iz]); _huDiagOnce = false; }
      if (ix < 0 || ix >= dims[0] || iy < 0 || iy >= dims[1] || iz < 0 || iz >= dims[2]) return null;
      const linearIdx = ix + iy * dims[0] + iz * dims[0] * dims[1];
      return (linearIdx >= 0 && linearIdx < sd.length) ? sd[linearIdx] : null;
    } catch(e) { return null; }
  }
  if (vp.type === ViewportType.STACK) {
    try {
      const imgId = vp.getCurrentImageId?.();
      const image = imgId ? cornerstone.cache.getImage?.(imgId) : null;
      if (!image) return null;
      let pixelCoords;
      try { pixelCoords = vp.worldToCanvas?.(worldPos); } catch (e) { pixelCoords = null; }
      if (!pixelCoords || pixelCoords.some(v => !Number.isFinite(v))) pixelCoords = canvasPoint;
      const col = Math.round(pixelCoords[0]), row = Math.round(pixelCoords[1]);
      if (col < 0 || col >= image.columns || row < 0 || row >= image.rows) return null;
      const pd = image.getPixelData?.();
      return pd ? pd[row * image.columns + col] * (image.slope || 1) + (image.intercept || 0) : null;
    } catch { return null; }
  }
  return null;
}

// ══════════════════════════════════════════════════════════════
//  LINE PROFILE + ВИЗУАЛИЗАЦИЯ
// ══════════════════════════════════════════════════════════════

export function computeLineProfile(viewportId, startCanvas, endCanvas, numSamples = 100) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;

  try {
    const startWorld = vp.canvasToWorld(startCanvas);
    const endWorld = vp.canvasToWorld(endCanvas);
    const distanceMm = Math.hypot(endWorld[0] - startWorld[0], endWorld[1] - startWorld[1], endWorld[2] - startWorld[2]);

    const samples = [], positions = [];
    for (let i = 0; i < numSamples; i++) {
      const t = i / (numSamples - 1);
      const worldPt = [
        startWorld[0] + t * (endWorld[0] - startWorld[0]),
        startWorld[1] + t * (endWorld[1] - startWorld[1]),
        startWorld[2] + t * (endWorld[2] - startWorld[2]),
      ];
      const canvasPt = [
        startCanvas[0] + t * (endCanvas[0] - startCanvas[0]),
        startCanvas[1] + t * (endCanvas[1] - startCanvas[1]),
      ];
      const hu = _getHUAtWorld(vp, worldPt, canvasPt);
      samples.push(hu !== null ? hu : NaN);
      positions.push(worldPt);
    }

    const valid = samples.filter(v => !isNaN(v));
    if (!valid.length) return null;

    const n = valid.length;
    const min = Math.min(...valid), max = Math.max(...valid);
    const mean = valid.reduce((a, b) => a + b, 0) / n;
    const std = n > 1 ? Math.sqrt(valid.reduce((a, v) => a + (v - mean) ** 2, 0) / (n - 1)) : 0;

    // Поиск границ (градиентный анализ)
    const boundaries = [];
    for (let i = 5; i < samples.length - 5; i++) {
      const grad = Math.abs((samples[i+5] || samples[i]) - (samples[i-5] || samples[i]));
      if (grad > 50 && isFinite(samples[i])) {
        boundaries.push({ index: i, hu: samples[i], gradient: +grad.toFixed(1), positionMm: +(i / (numSamples - 1) * distanceMm).toFixed(1) });
      }
    }

    const result = {
      samples, positions, distanceMm, numSamples,
      min: Math.round(min), max: Math.round(max), mean: +mean.toFixed(1), std: +std.toFixed(1),
      boundaries: boundaries.slice(0, 10),
    };

    _storeMeasurement('lineProfile', result);
    events.emit(EVENTS.LINE_PROFILE, { viewportId, ...result });
    return result;
  } catch (e) { log.warn('[LineProfile]', e.message); return null; }
}

/**
 * Отрисовать график профиля плотности в Canvas.
 * @param {Object} profileResult — результат computeLineProfile
 * @param {HTMLCanvasElement} canvas — целевой canvas
 * @param {Object} [opts] — { width, height, lineColor, fillColor }
 */
export function renderLineProfileChart(profileResult, canvas, opts = {}) {
  if (!profileResult?.samples?.length || !canvas) return;

  const width = opts.width || canvas.width || 400;
  const height = opts.height || canvas.height || 150;
  const lineColor = opts.lineColor || '#4fc3f7';
  const fillColor = opts.fillColor || 'rgba(79,195,247,0.15)';

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  const pad = { top: 10, right: 15, bottom: 25, left: 45 };
  const gw = width - pad.left - pad.right;
  const gh = height - pad.top - pad.bottom;

  // Фон
  ctx.fillStyle = '#0a0a14';
  ctx.fillRect(0, 0, width, height);

  const samples = profileResult.samples;
  const valid = samples.filter(v => isFinite(v));
  if (!valid.length) return;

  const minVal = Math.min(...valid);
  const maxVal = Math.max(...valid);
  const range = maxVal - minVal || 1;

  // Сетка
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const y = pad.top + (gh / 4) * i;
    ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + gw, y); ctx.stroke();
  }

  // Оси
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top); ctx.lineTo(pad.left, pad.top + gh);
  ctx.lineTo(pad.left + gw, pad.top + gh);
  ctx.stroke();

  // Подписи
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '9px monospace';
  ctx.textAlign = 'right';
  for (let i = 0; i <= 4; i++) {
    const val = minVal + (range / 4) * (4 - i);
    ctx.fillText(Math.round(val).toString(), pad.left - 5, pad.top + (gh / 4) * i + 3);
  }
  ctx.textAlign = 'center';
  ctx.fillText('0 mm', pad.left, pad.top + gh + 14);
  ctx.fillText(`${profileResult.distanceMm.toFixed(0)} mm`, pad.left + gw, pad.top + gh + 14);

  // Заливка
  ctx.beginPath();
  ctx.moveTo(pad.left, pad.top + gh);
  for (let i = 0; i < samples.length; i++) {
    if (!isFinite(samples[i])) continue;
    const x = pad.left + (i / (samples.length - 1)) * gw;
    const y = pad.top + (1 - (samples[i] - minVal) / range) * gh;
    ctx.lineTo(x, y);
  }
  ctx.lineTo(pad.left + gw, pad.top + gh);
  ctx.closePath();
  ctx.fillStyle = fillColor;
  ctx.fill();

  // Линия
  ctx.beginPath();
  let started = false;
  for (let i = 0; i < samples.length; i++) {
    if (!isFinite(samples[i])) { started = false; continue; }
    const x = pad.left + (i / (samples.length - 1)) * gw;
    const y = pad.top + (1 - (samples[i] - minVal) / range) * gh;
    if (!started) { ctx.moveTo(x, y); started = true; }
    else ctx.lineTo(x, y);
  }
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Границы
  if (profileResult.boundaries?.length) {
    for (const b of profileResult.boundaries) {
      const x = pad.left + (b.index / (samples.length - 1)) * gw;
      ctx.beginPath();
      ctx.moveTo(x, pad.top);
      ctx.lineTo(x, pad.top + gh);
      ctx.strokeStyle = 'rgba(255,100,100,0.5)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }
}

// ══════════════════════════════════════════════════════════════
//  TISSUE DENSITY MAP
// ══════════════════════════════════════════════════════════════

export function computeTissueDensityMap(huValues) {
  if (!huValues?.length) return [];
  const counts = new Map();
  for (const hu of huValues) {
    if (isNaN(hu)) continue;
    const t = classifyTissue(hu);
    counts.set(t.name, (counts.get(t.name) || 0) + 1);
  }
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return [...counts.entries()]
    .map(([tissue, count]) => ({ tissue, count, percent: +(count / total * 100).toFixed(1) }))
    .sort((a, b) => b.percent - a.percent);
}

// ══════════════════════════════════════════════════════════════
//  VOLUME STATISTICS
// ══════════════════════════════════════════════════════════════

export async function computeVolumeStats() {
  try {
    const sd = _getVolumeScalarData();
    if (!sd) throw new Error('No volume loaded');
  
    const step = sd.length > 2000000 ? Math.ceil(sd.length / 2000000) : 1;
    const sampled = [];
    for (let i = 0; i < sd.length; i += step) sampled.push(sd[i]);
    sampled.sort((a, b) => a - b);
  
    const n = sampled.length;
    const p = (pct) => sampled[Math.floor(n * pct / 100)];
    const mean = sampled.reduce((a, b) => a + b, 0) / n;
    const std = n > 1 ? Math.sqrt(sampled.reduce((a, v) => a + (v - mean) ** 2, 0) / (n - 1)) : 0;
  
    const result = {
      mean: +mean.toFixed(1), std: +std.toFixed(1),
      min: sampled[0], max: sampled[n - 1],
      p5: p(5), p25: p(25), median: p(50), p75: p(75), p95: p(95),
      voxelCount: sd.length, sampledCount: n,
      bodyRegion: _detectBodyRegion(sampled),
    };

    _storeMeasurement('volumeStats', result);
    events.emit(EVENTS.VOLUME_STATS, result);
    log.debug(`[VolumeStats] mean=${result.mean} region=${result.bodyRegion}`);
    return result;
  } catch (e) { log.warn(`[viewportAnalysis] computeVolumeStats failed:`, e.message); throw e; }
}

function _detectBodyRegion(sortedValues) {
  const n = sortedValues.length;
  const p25 = sortedValues[Math.floor(n * 0.25)];
  const p75 = sortedValues[Math.floor(n * 0.75)];
  const median = sortedValues[Math.floor(n * 0.5)];
  if (median < -400) return 'Грудная клетка (лёгкие)';
  if (p25 > 10 && p75 < 80 && median > 20 && median < 50) return 'Головной мозг';
  if (median > -20 && median < 60 && p75 < 200) return 'Брюшная полость';
  if (p75 > 300) return 'MSK / Кости';
  if (p75 > 150 && median > 40) return 'Ангиография';
  return 'Общее';
}

function _getVolumeScalarData() {
  const re = getRenderingEngine();
  if (!re) return null;
  for (const vpId of getViewportIds()) {
    const vp = re.getViewport(vpId);
    if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
      const actors = vp.getActors?.();
      if (actors?.length) {
        const a = actors[0];
        const volId = a.uid || a.referenceId || a.referencedId || a.volumeId || a.id;
        if (!volId) continue;
        let vol = null;
        try { vol = cornerstone.cache.getVolume?.(volId); } catch(e) {}
        if (!vol) try { const volumes = cornerstone.cache.getVolumes?.() || []; if (volumes.length) vol = volumes[0]; } catch(e) {}
        if (vol) {
          let sd = null;
          try { sd = vol.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) {}
          if (!sd && vol.imageData) try { sd = vol.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) {}
          if (!sd) try { sd = vol.getScalarData?.(); } catch (e) {}
          if (!sd) try { sd = vol.scalarData; } catch (e) {}
          return sd;
        }
      }
    }
  }
  return null;
}

// ══════════════════════════════════════════════════════════════
//  CIRCLE / RECT ROI STATS + СРАВНЕНИЕ
// ══════════════════════════════════════════════════════════════

export function computeCircleROIStats(viewportId, centerCanvas, radiusPx) {
  const vp = getRenderingEngine()?.getViewport(viewportId);
  if (!vp) return null;
  try {
    const centerWorld = vp.canvasToWorld(centerCanvas);
    const edgeWorld = vp.canvasToWorld([centerCanvas[0] + radiusPx, centerCanvas[1]]);
    const radiusWorld = Math.hypot(edgeWorld[0] - centerWorld[0], edgeWorld[1] - centerWorld[1], edgeWorld[2] - centerWorld[2]);

    const values = [];

    if (vp.type === ViewportType.ORTHOGRAPHIC) {
      const imageData = vp.getImageData?.();
      if (!imageData) return null;
      const dims = imageData.getDimensions?.() || imageData.dimensions;
      if (!dims) return null;
      const spacing = imageData.getSpacing?.() || [1,1,1];
      const sd = imageData.getPointData?.()?.getScalars?.()?.getData?.();
      if (!sd) return null;
      const ci = imageData.worldToIndex(centerWorld);
      const ri = [radiusWorld / spacing[0], radiusWorld / spacing[1], radiusWorld / spacing[2]];
      for (let dz = -Math.ceil(ri[2]); dz <= Math.ceil(ri[2]); dz++)
        for (let dy = -Math.ceil(ri[1]); dy <= Math.ceil(ri[1]); dy++)
          for (let dx = -Math.ceil(ri[0]); dx <= Math.ceil(ri[0]); dx++) {
            if ((dx / (ri[0] || 1)) ** 2 + (dy / (ri[1] || 1)) ** 2 + (dz / (ri[2] || 1)) ** 2 > 1) continue;
            const [ix, iy, iz] = [Math.round(ci[0] + dx), Math.round(ci[1] + dy), Math.round(ci[2] + dz)];
            if (ix >= 0 && ix < dims[0] && iy >= 0 && iy < dims[1] && iz >= 0 && iz < dims[2])
              values.push(sd[ix + iy * dims[0] + iz * dims[0] * dims[1]]);
          }
    } else if (vp.type === ViewportType.STACK) {
      const image = cornerstone.cache.getImage?.(vp.getCurrentImageId?.());
      if (!image) return null;
      const pd = image.getPixelData?.();
      if (!pd) return null;
      const { columns: w, rows: h, columnPixelSpacing = 1, rowPixelSpacing = 1, slope = 1, intercept = 0 } = image;
      const scale = vp.getZoom?.() || 1;
      const rPx = radiusPx / scale;
      const cx = Math.round(centerCanvas[0] / scale), cy = Math.round(centerCanvas[1] / scale);
      for (let dy = -Math.ceil(rPx); dy <= Math.ceil(rPx); dy++)
        for (let dx = -Math.ceil(rPx); dx <= Math.ceil(rPx); dx++) {
          if (dx * dx + dy * dy > rPx * rPx) continue;
          const px = cx + dx, py = cy + dy;
          if (px >= 0 && px < w && py >= 0 && py < h)
            values.push(pd[py * w + px] * slope + intercept);
        }
    }

    if (!values.length) return null;
    const count = values.length;
    const min = Math.min(...values), max = Math.max(...values);
    const mean = values.reduce((a, b) => a + b, 0) / count;
    const std = count > 1 ? Math.sqrt(values.reduce((a, v) => a + (v - mean) ** 2, 0) / (count - 1)) : 0;
    const areaMm2 = Math.PI * radiusWorld * radiusWorld;
    const volumeMl = areaMm2 * 0; // для 2D ROI объём не считаем

    const densityMap = computeTissueDensityMap(values);
    const dominantTissue = densityMap[0]?.tissue || 'N/A';

    const result = {
      mean: +mean.toFixed(1), std: +std.toFixed(1), min: Math.round(min), max: Math.round(max),
      count, areaMm2: +areaMm2.toFixed(2), volumeMl, radiusWorld, centerWorld,
      dominantTissue, densityMap,
    };

    _storeMeasurement('circleROI', result);
    events.emit(EVENTS.ROI_STATS, { viewportId, ...result });
    return result;
  } catch (e) { log.warn('[ROI]', e.message); return null; }
}

export function computeRectROIStats(viewportId, topLeft, bottomRight) {
  const center = [(topLeft[0] + bottomRight[0]) / 2, (topLeft[1] + bottomRight[1]) / 2];
  const radius = Math.max(Math.abs(bottomRight[0] - topLeft[0]), Math.abs(bottomRight[1] - topLeft[1])) / 2;
  return computeCircleROIStats(viewportId, center, radius);
}

/**
 * Сравнить два ROI (текущий и предыдущий).
 * @param {Object} currentROI — результат computeCircleROIStats
 * @param {Object} priorROI — результат computeCircleROIStats (из истории)
 * @returns {Object} { deltaAreaMm2, deltaMeanHU, percentChangeArea, percentChangeMean }
 */
export function compareROIStats(currentROI, priorROI) {
  if (!currentROI || !priorROI) return null;
  const deltaAreaMm2 = +(currentROI.areaMm2 - priorROI.areaMm2).toFixed(2);
  const deltaMeanHU = +(currentROI.mean - priorROI.mean).toFixed(1);
  const percentChangeArea = priorROI.areaMm2 > 0 ? +((deltaAreaMm2 / priorROI.areaMm2) * 100).toFixed(1) : 0;
  const percentChangeMean = priorROI.mean !== 0 ? +((deltaMeanHU / Math.abs(priorROI.mean)) * 100).toFixed(1) : 0;

  const result = { deltaAreaMm2, deltaMeanHU, percentChangeArea, percentChangeMean, current: currentROI, prior: priorROI };
  _storeMeasurement('roiComparison', result);
  return result;
}

// ══════════════════════════════════════════════════════════════
//  CALCIUM SCORING — делегирует в aiModule.js
// ══════════════════════════════════════════════════════════════

export async function computeCalciumScore(opts = {}) {
  try {
    try {
      const { calciumScoring } = await import('../ai/aiModule.js');
      if (typeof calciumScoring === 'function') {
        const sd = _getVolumeScalarData();
        if (!sd) throw new Error('No volume data');
        const re = getRenderingEngine();
        let dims = [512, 512, 1], spacing = [1, 1, 1];
        for (const vpId of getViewportIds()) {
          const vp = re?.getViewport(vpId);
          if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
            const actors = vp.getActors?.();
            if (actors?.length) {
              const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
              const vol = volId ? cornerstone.cache.getVolume?.(volId) : null;
              if (vol) { dims = vol.dimensions || dims; spacing = vol.spacing || spacing; break; }
            }
          }
        }
        const result = await calciumScoring(sd, dims[0], dims[1], dims[2], spacing, {
          huThreshold: opts.threshold || 130,
          minVoxels: opts.minVoxels || 3,
          cardiacROI: opts.cardiacROI !== false,
          onProgress: opts.onProgress,
        });
        _storeMeasurement('calciumScore', result);
        events.emit(EVENTS.CALCIUM_SCORE_DONE, result);
        return result;
      }
    } catch (e) { log.warn('[CalciumScore] 3D engine unavailable, falling back to 2D:', e.message); }
    return await _computeCalciumScore2D(opts);
  } catch (e) { log.warn(`[viewportAnalysis] computeCalciumScore failed:`, e.message); throw e; }
}

async function _computeCalciumScore2D(opts = {}) {
  const threshold = opts.threshold || 130;
  const minAreaMm2 = opts.minAreaMm2 || 1;
  const re = getRenderingEngine();
  if (!re) throw new Error('RenderingEngine not initialized');
  let volume = null, volumeId = opts.volumeId || null;
  for (const vpId of getViewportIds()) {
    const vp = re.getViewport(vpId);
    if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
      const actors = vp.getActors?.();
      if (actors?.length) { volumeId = volumeId || actors[0].uid || actors[0].referenceId || actors[0].referencedId; break; }
    }
  }
  if (volumeId) try { volume = cornerstone.cache.getVolume?.(volumeId); } catch(e) {}
  if (!volume) try { const volumes = cornerstone.cache.getVolumes?.() || []; if (volumes.length) volume = volumes[0]; } catch(e) {}
  if (!volume) throw new Error('No volume loaded');

  let sd = null;
  try { sd = volume.voxelManager?.getCompleteScalarDataArray?.(); } catch (e) {}
  if (!sd && volume.imageData) try { sd = volume.imageData.getPointData?.()?.getScalars?.()?.getData?.(); } catch (e) {}
  if (!sd) try { sd = volume.getScalarData?.(); } catch (e) {}
  if (!sd) throw new Error('Cannot access scalar data');

  const dims = volume.dimensions || (volume.imageData?.getDimensions?.() || volume.imageData?.dimensions) || [512, 512, 1];
  const spacing = volume.spacing || volume.imageData?.getSpacing?.() || [1, 1, 1];
  const [cols, rows, slices] = dims;
  const pixelAreaMm2 = spacing[0] * spacing[1];

  const perSlice = [];
  let totalScore = 0;
  for (let z = 0; z < slices; z++) {
    const off = z * rows * cols;
    const visited = new Uint8Array(rows * cols);
    let sliceScore = 0;
    const clusters = [];
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < cols; x++) {
        const idx = y * cols + x;
        if (visited[idx] || sd[off + idx] < threshold) continue;
        visited[idx] = 1;
        const queue = [idx];
        let size = 0, peakHU = sd[off + idx], head = 0;
        while (head < queue.length) {
          const ci = queue[head++]; size++;
          const cv = sd[off + ci]; if (cv > peakHU) peakHU = cv;
          const cx = ci % cols, cy = (ci / cols) | 0;
          for (const ni of [cx > 0 ? ci - 1 : -1, cx < cols - 1 ? ci + 1 : -1, cy > 0 ? ci - cols : -1, cy < rows - 1 ? ci + cols : -1]) {
            if (ni >= 0 && !visited[ni] && sd[off + ni] >= threshold) { visited[ni] = 1; queue.push(ni); }
          }
        }
        const area = size * pixelAreaMm2;
        if (area < minAreaMm2) continue;
        const weight = peakHU >= 400 ? 4 : peakHU >= 300 ? 3 : peakHU >= 200 ? 2 : 1;
        sliceScore += area * weight;
        clusters.push({ areaMm2: +area.toFixed(2), peakHU, weight, pixelCount: size });
      }
    if (sliceScore > 0) { perSlice.push({ slice: z, score: +sliceScore.toFixed(1), clusters: clusters.slice(0, 20) }); totalScore += sliceScore; }
    if (z % 10 === 0) await new Promise(r => setTimeout(r, 0));
  }

  totalScore = Math.round(totalScore);
  const riskCategory = totalScore === 0 ? 'Нет кальциноза' : totalScore <= 10 ? 'Минимальный' : totalScore <= 100 ? 'Лёгкий' : totalScore <= 400 ? 'Умеренный' : 'Выраженный';
  const result = {
    totalScore, riskCategory,
    perSlice: perSlice.slice(0, 100),
    totalClusters: perSlice.reduce((s, sl) => s + sl.clusters.length, 0),
    volumeInfo: { dims, spacing, slices, pixelAreaMm2, sliceThicknessMm: spacing[2] },
    threshold, timestamp: Date.now(),
  };
  _storeMeasurement('calciumScore', result);
  events.emit(EVENTS.CALCIUM_SCORE_DONE, result);
  return result;
}

// ══════════════════════════════════════════════════════════════
//  PATHOLOGY FINDER
// ══════════════════════════════════════════════════════════════

export async function jumpToPathology(opts = {}) {
  try {
    const threshold = opts.threshold || 130;
    const re = getRenderingEngine();
    if (!re) return null;
  
    let sd = null, dims = null;
    for (const id of getViewportIds()) {
      const vp = re.getViewport(id);
      if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
        try {
          const imageData = vp.getImageData?.();
          if (imageData) { sd = imageData.getPointData?.()?.getScalars?.()?.getData?.(); dims = imageData.getDimensions?.() || imageData.dimensions; break; }
        } catch (e) { log.debug("[viewportAnalysis]", e.message); }
      }
    }
    if (!sd || !dims) return null;
  
    const [cols, rows, slices] = dims;
    let maxCount = 0, maxSlice = 0, maxPeakHU = 0;
    for (let z = 0; z < slices; z++) {
      const off = z * rows * cols;
      let count = 0, peak = threshold;
      for (let i = off; i < off + rows * cols; i += 4) { if (sd[i] >= threshold) { count++; if (sd[i] > peak) peak = sd[i]; } }
      if (count > maxCount) { maxCount = count; maxSlice = z; maxPeakHU = peak; }
      if (z % 50 === 0) await new Promise(r => setTimeout(r, 0));
    }
  
    if (maxCount === 0) { showToast('Патология не обнаружена', 'info', 2000); return null; }
  
    const vpId = opts.viewportId || getViewportIds()[0];
    const vp = re.getViewport(vpId);
    if (vp) {
      if (vp.type === ViewportType.STACK) try { vp.setImageIdIndex(maxSlice); vp.render(); } catch (e) {}
      else try {
        const imageData = vp.getImageData?.();
        const spacing = imageData?.getSpacing?.();
        const origin = imageData?.getOrigin?.();
        const cam = vp.getCamera();
        if (cam && spacing && origin) {
          const vpn = cam.viewPlaneNormal;
          const axis = Math.abs(vpn[0]) > 0.5 ? 0 : Math.abs(vpn[1]) > 0.5 ? 1 : 2;
          const dist = origin[axis] + maxSlice * spacing[axis] - cam.focalPoint[axis];
          const newFp = [...cam.focalPoint], newPos = [...cam.position];
          newFp[axis] += dist; newPos[axis] += dist;
          vp.setCamera({ focalPoint: newFp, position: newPos }); vp.render();
        }
      } catch (e) {}
    }
  
    const result = { sliceIndex: maxSlice, count: maxCount * 4, peakHU: maxPeakHU, threshold };
    events.emit(EVENTS.PATHOLOGY_FOUND, result);
    showToast(`🎯 Патология: срез #${maxSlice} (пик ${maxPeakHU} HU)`, 'success', 3000);
    return result;
  } catch (e) { log.warn(`[viewportAnalysis] jumpToPathology failed:`, e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  AORTIC ANEURYSM DETECTION
// ══════════════════════════════════════════════════════════════

/**
 * Автоматический скрининг аневризмы аорты.
 * Использует aiModuleCore.aortaAneurysmScreening или упрощённый fallback.
 * @returns {Promise<Object>} { maxDiameterMm, aneurysmWarning, measurements }
 */
export async function detectAorticAneurysm() {
  try {
    const sd = _getVolumeScalarData();
    if (!sd) throw new Error('No volume data');

    // Пробуем aiModuleCore
    try {
      const { aortaAneurysmScreening } = await import('../ai/aiModuleCore.js');
      if (typeof aortaAneurysmScreening === 'function') {
        const re = getRenderingEngine();
        let dims = [512, 512, 1], spacing = [1, 1, 1];
        for (const vpId of getViewportIds()) {
          const vp = re?.getViewport(vpId);
          if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
            const actors = vp.getActors?.();
            if (actors?.length) {
              const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
              const vol = volId ? cornerstone.cache.getVolume?.(volId) : null;
              if (vol) { dims = vol.dimensions || dims; spacing = vol.spacing || spacing; break; }
            }
          }
        }
        const result = aortaAneurysmScreening(sd, dims[0], dims[1], dims[2], spacing);
        _storeMeasurement('aorticAneurysm', result);
        return result;
      }
    } catch (e) { log.warn('[Aorta] aiModuleCore unavailable, using fallback:', e.message); }

    // Fallback: простой поиск круглых структур 30–300 HU
    return _detectAorticAneurysmFallback(sd);
  } catch (e) { log.warn('[Aorta] detection failed:', e.message); return null; }
}

function _detectAorticAneurysmFallback(sd) {
  // Упрощённый поиск аорты: круглая структура 30–300 HU в центре средних срезов
  let maxDiam = 0;
  const sliceSize = 512 * 512; // предполагаем
  const w = 512, h = 512;
  const d = Math.floor(sd.length / sliceSize) || 1;
  const spacing = [1, 1, 1];

  for (let z = Math.floor(d * 0.3); z < Math.floor(d * 0.7); z++) {
    let aArea = 0;
    for (let y = Math.floor(h * 0.35); y < Math.floor(h * 0.65); y++) {
      for (let x = Math.floor(w * 0.35); x < Math.floor(w * 0.65); x++) {
        const hu = sd[z * sliceSize + y * w + x];
        if (hu > 30 && hu < 300) aArea++;
      }
    }
    if (aArea < 10) continue;
    const diamMm = Math.sqrt(aArea * spacing[0] * spacing[1] / Math.PI) * 2;
    if (diamMm > maxDiam) maxDiam = diamMm;
  }

  let warning = 'Норма';
  if (maxDiam > 55) warning = '⚠️ КРИТИЧЕСКОЕ расширение аорты (>5.5 см)';
  else if (maxDiam > 45) warning = '⚠️ Значительное расширение аорты (>4.5 см)';
  else if (maxDiam > 35) warning = '⚠️ Умеренное расширение аорты (>3.5 см)';

  const result = { maxDiameterMm: +maxDiam.toFixed(1), aneurysmWarning: warning, method: 'fallback 2D' };
  _storeMeasurement('aorticAneurysm', result);
  return result;
}

// ══════════════════════════════════════════════════════════════
//  RECIST 1.1 + TUMOR VOLUME
// ══════════════════════════════════════════════════════════════

/**
 * Вычислить RECIST 1.1 (сумма диаметров целевых очагов).
 * @param {number[]} diametersMm — массив диаметров целевых очагов в мм
 * @returns {Object} { sumDiametersMm, targetLesions, response }
 */
export function computeRECIST(diametersMm) {
  if (!diametersMm?.length) return null;
  const sumDiameters = +diametersMm.reduce((a, b) => a + b, 0).toFixed(1);
  const result = {
    sumDiametersMm: sumDiameters,
    targetLesions: diametersMm.length,
    individualLesions: diametersMm.map((d, i) => ({ lesion: i + 1, diameterMm: d })),
  };
  _storeMeasurement('recist', result);
  return result;
}

/**
 * Оценить ответ на лечение по RECIST 1.1.
 * @param {number} currentSum — текущая сумма диаметров (мм)
 * @param {number} priorSum — предыдущая сумма диаметров (мм)
 * @returns {Object} { percentChange, response, priorSum, currentSum }
 */
export function computeRECISTResponse(currentSum, priorSum) {
  if (!priorSum || priorSum === 0) return null;
  const percentChange = +(((currentSum - priorSum) / priorSum) * 100).toFixed(1);

  let response;
  if (percentChange <= -30) response = 'Частичный ответ (PR)';
  else if (percentChange >= 20) response = 'Прогрессирование (PD)';
  else response = 'Стабилизация (SD)';

  const result = { percentChange, response, priorSum, currentSum };
  _storeMeasurement('recistResponse', result);
  return result;
}

/**
 * Изменение объёма опухоли (использует tumorKinetics из aiModule).
 * @param {number} currentVolMl — текущий объём (мл)
 * @param {number} priorVolMl — предыдущий объём (мл)
 * @param {number} daysBetween — дней между исследованиями
 * @returns {Object}
 */
export async function computeTumorVolumeChange(currentVolMl, priorVolMl, daysBetween) {
  try {
    // Пробуем использовать tumorKinetics из aiModuleCore
    try {
      const module = await import('../ai/aiModuleCore.js');
      const { tumorKinetics } = module;
      if (typeof tumorKinetics === 'function') {
        const result = tumorKinetics(priorVolMl, currentVolMl, daysBetween);
        _storeMeasurement('tumorVolumeChange', result);
        return result;
      }
    } catch (e) { log.warn('[TumorVolume] aiModuleCore unavailable:', e.message); }

    // Fallback
    const growth = priorVolMl > 0 ? +(((currentVolMl - priorVolMl) / priorVolMl) * 100).toFixed(1) : 0;
    const doublingTime = priorVolMl > 0 && currentVolMl > priorVolMl
      ? Math.round(daysBetween * Math.log(2) / Math.log(currentVolMl / priorVolMl))
      : null;

    const result = { growthPercent: growth, doublingTimeDays: doublingTime, priorVolMl, currentVolMl, daysBetween };
    _storeMeasurement('tumorVolumeChange', result);
    return result;
  } catch (e) { log.warn('[TumorVolume] calculation failed:', e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  BRAIN ATROPHY ANALYSIS
// ══════════════════════════════════════════════════════════════

/**
 * Автоматический анализ атрофии мозга (VBR).
 * @returns {Promise<Object>} { atrophyIndex, warning, regions }
 */
export async function analyzeBrainAtrophy() {
  try {
    const sd = _getVolumeScalarData();
    if (!sd) throw new Error('No volume data');

    try {
      const { segmentBrainByHU } = await import('../ai/aiModuleCore.js');
      if (typeof segmentBrainByHU === 'function') {
        const re = getRenderingEngine();
        let dims = [512, 512, 1], spacing = [1, 1, 1];
        for (const vpId of getViewportIds()) {
          const vp = re?.getViewport(vpId);
          if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
            const actors = vp.getActors?.();
            if (actors?.length) {
              const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
              const vol = volId ? cornerstone.cache.getVolume?.(volId) : null;
              if (vol) { dims = vol.dimensions || dims; spacing = vol.spacing || spacing; break; }
            }
          }
        }
        const result = await segmentBrainByHU(sd, dims[0], dims[1], dims[2], spacing);
        _storeMeasurement('brainAtrophy', result);
        return result;
      }
    } catch (e) { log.warn('[Brain] aiModuleCore unavailable:', e.message); }

    return { error: 'Brain segmentation module unavailable' };
  } catch (e) { log.warn('[Brain] analysis failed:', e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  ORGAN SEGMENTATION BY DENSITY
// ══════════════════════════════════════════════════════════════

/**
 * Сегментация органов по плотности HU.
 * @returns {Promise<Object>} { organs: { name: { voxels, volumeMl, meanHU } } }
 */
export async function segmentOrgansByDensity() {
  try {
    const sd = _getVolumeScalarData();
    if (!sd) throw new Error('No volume data');

    try {
      const { segmentOrgansByHU } = await import('../ai/aiModuleCore.js');
      if (typeof segmentOrgansByHU === 'function') {
        const re = getRenderingEngine();
        let dims = [512, 512, 1], spacing = [1, 1, 1];
        for (const vpId of getViewportIds()) {
          const vp = re?.getViewport(vpId);
          if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
            const actors = vp.getActors?.();
            if (actors?.length) {
              const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
              const vol = volId ? cornerstone.cache.getVolume?.(volId) : null;
              if (vol) { dims = vol.dimensions || dims; spacing = vol.spacing || spacing; break; }
            }
          }
        }
        const result = await segmentOrgansByHU(sd, dims[0], dims[1], dims[2], spacing);
        _storeMeasurement('organSegmentation', result);
        return result;
      }
    } catch (e) { log.warn('[Organs] aiModuleCore unavailable:', e.message); }

    return { error: 'Organ segmentation module unavailable' };
  } catch (e) { log.warn('[Organs] segmentation failed:', e.message); return null; }
}

// ══════════════════════════════════════════════════════════════
//  CRITICAL FINDINGS DETECTOR
// ══════════════════════════════════════════════════════════════

/**
 * Автоматический поиск критических находок.
 * Ищет: кровоизлияния (>50 HU в мозге), пневмоторакс (воздух в плевральной полости),
 * кальцинаты коронарных артерий (>130 HU в зоне сердца).
 *
 * @returns {Promise<Object[]>} массив найденных патологий
 */
export async function autoDetectCriticalFindings() {
  try {
    const sd = _getVolumeScalarData();
    if (!sd) throw new Error('No volume data');

    const findings = [];
    const re = getRenderingEngine();
    let dims = [512, 512, 1], spacing = [1, 1, 1];

    for (const vpId of getViewportIds()) {
      const vp = re?.getViewport(vpId);
      if (vp?.type === ViewportType.ORTHOGRAPHIC || vp?.type === ViewportType.VOLUME_3D) {
        const actors = vp.getActors?.();
        if (actors?.length) {
          const volId = actors[0]?.uid || actors[0]?.referenceId || actors[0]?.referencedId;
          const vol = volId ? cornerstone.cache.getVolume?.(volId) : null;
          if (vol) { dims = vol.dimensions || dims; spacing = vol.spacing || spacing; break; }
        }
      }
    }

    const [w, h, d] = dims;
    const sliceSize = w * h;

    // Определяем регион тела
    const bodyRegion = _detectBodyRegionQuick(sd, dims);

    // 1. Кровоизлияние в мозге (HEAD)
    if (bodyRegion === 'HEAD') {
      let hyperdenseCount = 0;
      const zStart = Math.floor(d * 0.2), zEnd = Math.floor(d * 0.8);
      for (let z = zStart; z < zEnd; z++) {
        for (let i = z * sliceSize; i < (z + 1) * sliceSize; i += 4) {
          if (sd[i] >= 50 && sd[i] <= 100) hyperdenseCount++;
        }
      }
      if (hyperdenseCount > 500) {
        findings.push({
          pathology: '⚠️ Подозрение на внутримозговое кровоизлияние',
          severity: 'CRITICAL',
          detail: `Обнаружено ${hyperdenseCount} гиперденсных вокселей (50–100 HU) в зоне мозга`,
          recommendation: 'Рекомендована срочная консультация нейрорадиолога',
        });
      }
    }

    // 2. Пневмоторакс (CHEST)
    if (bodyRegion === 'CHEST') {
      // Ищем воздух (−1000...−900 HU) в зоне плевры (передняя часть грудной клетки)
      let pleuralAirCount = 0;
      for (let z = Math.floor(d * 0.3); z < Math.floor(d * 0.7); z++) {
        const yStart = Math.floor(h * 0.1), yEnd = Math.floor(h * 0.3); // передняя часть
        for (let y = yStart; y < yEnd; y++) {
          for (let x = Math.floor(w * 0.1); x < Math.floor(w * 0.4); x++) {
            const hu = sd[z * sliceSize + y * w + x];
            if (hu >= -1000 && hu <= -900) pleuralAirCount++;
          }
        }
      }
      if (pleuralAirCount > 1000) {
        findings.push({
          pathology: '⚠️ Подозрение на пневмоторакс',
          severity: 'CRITICAL',
          detail: `Обнаружено ${pleuralAirCount} вокселей воздуха в плевральной области`,
          recommendation: 'Проверьте переднюю плевральную полость',
        });
      }
    }

    // 3. Кальцинаты коронарных артерий
    let coronaryCalciumCount = 0;
    const zHeartStart = Math.floor(d * 0.35), zHeartEnd = Math.floor(d * 0.65);
    for (let z = zHeartStart; z < zHeartEnd; z++) {
      for (let y = Math.floor(h * 0.3); y < Math.floor(h * 0.7); y++) {
        for (let x = Math.floor(w * 0.3); x < Math.floor(w * 0.7); x++) {
          const hu = sd[z * sliceSize + y * w + x];
          if (hu >= 130 && hu <= 1000) coronaryCalciumCount++;
        }
      }
    }
    if (coronaryCalciumCount > 100) {
      findings.push({
        pathology: 'ℹ️ Кальцинаты коронарных артерий',
        severity: 'MODERATE',
        detail: `Обнаружено ${coronaryCalciumCount} вокселей кальция (130–1000 HU) в зоне сердца`,
        recommendation: 'Рекомендован кальциевый скоринг (Агатстон)',
      });
    }

    if (findings.length === 0) {
      findings.push({ pathology: 'Критических находок не обнаружено', severity: 'NORMAL' });
    }

    _storeMeasurement('criticalFindings', { findings, bodyRegion });
    return { findings, bodyRegion, dimensions: dims, spacing };
  } catch (e) {
    log.warn('[CriticalFindings] detection failed:', e.message);
    return { error: e.message };
  }
}

function _detectBodyRegionQuick(sd, dims) {
  const [w, h, d] = dims;
  const sliceSize = w * h;
  const step = Math.max(1, Math.floor(sd.length / 50000));
  const samples = [];
  for (let i = 0; i < sd.length; i += step) samples.push(sd[i]);
  samples.sort((a, b) => a - b);
  return _detectBodyRegion(samples);
}